/** Application entry. Domain state is private to this instance; no window globals. */
import { register as registerConfig, configureSite } from './config.js';
import { register as registerLayout, prepareLayoutState, bindViewportEvents } from './layout.js';
import { register as registerNavigation, bindNavigation } from './navigation.js';
import {
  register as registerUi,
  initializeCursor,
  bindHoverEffects,
  bindTabs,
  initializeRevealObserver,
} from './ui.js';
import {
  register as registerHero,
  prepareIntroOwnership,
  initializeCarousel,
  bindHeroInteractions,
} from './hero.js';
import { register as registerProjects, initializeProjects } from './projects.js';
import { register as registerPreferences, initializePreferences } from './preferences.js';
import { bindActions } from './actions.js';
import { initializeCollaboration } from './collaboration.js';
import { initializeConsent } from './consent.js';
import measurement from '../content/measurement.json';

function initializeSite() {
  const app = {
    config: {},
    layout: {},
    navigation: {},
    ui: {},
    hero: {},
    projects: {},
    preferences: {},
  };
  const { config, layout, navigation, ui, hero, projects, preferences } = app;
  registerConfig(app);
  registerLayout(app);
  registerNavigation(app);
  registerUi(app);
  registerHero(app);
  registerProjects(app);
  registerPreferences(app);

  // Preserve startup ordering while each feature owns its implementation.
  configureSite(app);
  prepareIntroOwnership(app);
  prepareLayoutState(app);
  bindNavigation(app);
  initializeCursor(app);
  initializeCarousel(app);
  initializeProjects(app);
  bindHoverEffects(app);
  initializePreferences(app);
  bindTabs(app);
  initializeRevealObserver(app);
  bindHeroInteractions(app);
  bindViewportEvents(app);
  bindActions(app);
  layout.getHeaderHeight();
  preferences.setLanguage('sk');
  initializeCollaboration(app);
  const initialRoute = navigation.getRouteFromLocation();
  navigation.recordHistoryRoute(initialRoute, true);
  navigation.navigateFromHistory(initialRoute, { instant: true });
  initializeConsent(measurement, () => navigation.routeToHash(navigation.getRouteFromLocation()));
  ui.observeElements();
  layout.queueDotGridZoneUpdate();
  let wasHeaderCompact = null;
  let headerCompactRecalcTimer = 0;
  const onScroll = () => {
    const isCompact = layout.getScrollTop() > 40;
    if (isCompact) document.body.classList.add('header-compact');
    else document.body.classList.remove('header-compact');
    if (wasHeaderCompact === null || wasHeaderCompact !== isCompact) {
      wasHeaderCompact = isCompact;
      layout.queueDotGridZoneUpdate();
      clearTimeout(headerCompactRecalcTimer);
      headerCompactRecalcTimer = setTimeout(() => {
        layout.queueDotGridZoneUpdate();
      }, 420);
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  const headerEl = document.querySelector('header');
  if (headerEl) {
    headerEl.addEventListener('transitionend', (event) => {
      if (String(event.propertyName).includes('padding')) {
        layout.queueDotGridZoneUpdate();
      }
    });
  }
  hero.initializeIntro(initialRoute);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSite, { once: true });
} else {
  initializeSite();
}
